import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";
import Button from "../../../../componentes/button/button";
import RecorteQuatroPontos from "./RecorteQuatroPontos";
import { baixarArquivo, carregarAnexosParaPdf, montarPdfUnico } from "../../../../utils/solicitacoes/juntarAnexosEmPdf";
import { PONTOS_PADRAO, pontosSaoPadrao, recortarPerspectiva } from "../../../../utils/solicitacoes/recortePerspectiva";
import { rotacionarImagem, rotacionarPontos, somarRotacao } from "../../../../utils/solicitacoes/rotacao";

/**
 * Monta um PDF único com os anexos da solicitação na ordem informada, com duas etapas:
 * 1) edição — cada anexo pode ser girado de 90 em 90°, cada imagem pode ser recortada arrastando 4 cantos
 *    (o recorte "endireita" fotos tiradas tortas) e cada anexo pode ser deixado de fora;
 * 2) visualização — o PDF gerado aparece na janela antes de baixar.
 * Nada é gravado na solicitação: os anexos originais não mudam.
 */
export default function PdfUnicoModal({ visible, onHide, anexos = [], ordem = [], nomeArquivo = "documentos.pdf" }) {
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);
    const [itens, setItens] = useState([]);
    const [faltando, setFaltando] = useState([]);
    const [ignorados, setIgnorados] = useState([]);
    const [gerando, setGerando] = useState(false);
    const [pdf, setPdf] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);

    // Ao abrir: baixa os anexos na ordem e prepara cada imagem com os cantos na borda (sem recorte).
    useEffect(() => {
        if (!visible) return;
        let ativo = true;
        setCarregando(true);
        setErro(null);
        setPdf(null);
        carregarAnexosParaPdf(anexos, ordem)
            .then(r => {
                if (!ativo) return;
                // blobOriginal: base de cada giro (girar a partir da original não acumula perda de qualidade).
                setItens(r.itens.map(i => ({ ...i, blobOriginal: i.blob, rotacao: 0, girando: false, incluir: true, pontos: PONTOS_PADRAO })));
                setFaltando(r.faltando);
                setIgnorados(r.ignorados);
            })
            .catch(() => ativo && setErro("Não foi possível carregar os anexos."))
            .finally(() => ativo && setCarregando(false));
        return () => { ativo = false; };
        // recarrega a cada abertura da janela
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    useEffect(() => {
        if (!pdf) {
            setPdfUrl(null);
            return undefined;
        }
        const url = URL.createObjectURL(pdf);
        setPdfUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [pdf]);

    function atualizarItem(indice, campos) {
        setItens(prev => prev.map((item, i) => (i === indice ? { ...item, ...campos } : item)));
    }

    /**
     * Gira 90° (`delta` = 90 horário, -90 anti-horário). Imagem: é girada na hora (a tela mostra o resultado)
     * e os cantos do recorte giram junto. PDF: só guarda o giro — as páginas são giradas ao montar o PDF.
     */
    async function girar(indice, delta) {
        const item = itens[indice];
        const rotacao = somarRotacao(item.rotacao, delta);
        if (item.tipo === "pdf") {
            atualizarItem(indice, { rotacao });
            return;
        }
        atualizarItem(indice, { girando: true });
        try {
            const blob = rotacao === 0 ? item.blobOriginal : await rotacionarImagem(item.blobOriginal, rotacao);
            atualizarItem(indice, { blob, rotacao, pontos: rotacionarPontos(item.pontos, delta), girando: false });
        } catch (e) {
            atualizarItem(indice, { girando: false });
            toast.error(e?.message || "Não foi possível girar a imagem.");
        }
    }

    async function gerar() {
        const selecionados = itens.filter(i => i.incluir);
        if (selecionados.length === 0) {
            toast.warn("Selecione ao menos um anexo para o PDF.");
            return;
        }
        setGerando(true);
        try {
            const preparados = [];
            for (const item of selecionados) {
                const recortar = item.tipo === "imagem" && !pontosSaoPadrao(item.pontos);
                preparados.push(recortar ? { ...item, blob: await recortarPerspectiva(item.blob, item.pontos) } : item);
            }
            setPdf(await montarPdfUnico(preparados, nomeArquivo));
        } catch (e) {
            toast.error(e?.message || "Não foi possível gerar o PDF.");
        } finally {
            setGerando(false);
        }
    }

    const rodape = pdf ? (
        <div className="flex flex-wrap justify-end gap-2 pt-3">
            <Button variant="outline" label="Voltar e ajustar" iconLeft="fa fa-arrow-left" onClick={() => setPdf(null)} />
            <Button variant="blue" label="Baixar PDF" iconLeft="fa fa-download" onClick={() => baixarArquivo(pdf)} />
        </div>
    ) : (
        <div className="flex flex-wrap justify-end gap-2 pt-3">
            <Button variant="outline" label="Cancelar" onClick={onHide} />
            <Button variant="blue" label="Gerar e visualizar" iconLeft="fa fa-file-pdf" loading={gerando}
                disabled={gerando || carregando || itens.length === 0} onClick={gerar} />
        </div>
    );

    return (
        <Dialog
            header={pdf ? "Visualizar PDF único" : "PDF único — ajustar anexos"}
            visible={visible}
            onHide={onHide}
            blockScroll
            className="w-[96vw] max-w-5xl"
            footer={rodape}
        >
            {pdf ? (
                <iframe title="PDF único" src={pdfUrl || undefined} className="w-full h-[75vh] rounded-lg border border-border" />
            ) : (
                <div className="flex flex-col gap-4">
                    {carregando && <p className="py-6 text-center text-sm text-muted-foreground">Carregando anexos...</p>}
                    {erro && <p className="py-6 text-center text-sm text-destructive">{erro}</p>}

                    {!carregando && !erro && (
                        <>
                            <p className="text-sm text-muted-foreground">
                                Os anexos entram nesta ordem. Use os botões de girar para deixar cada anexo em pé e, nas imagens, arraste
                                os 4 cantos azuis até os cantos do documento para recortá-lo e endireitá-lo. Os anexos originais da
                                solicitação não são alterados.
                            </p>
                            {(faltando.length > 0 || ignorados.length > 0) && (
                                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
                                    {faltando.length > 0 && <span className="block">Sem anexo de: {faltando.join(", ")}.</span>}
                                    {ignorados.length > 0 && <span className="block">Não entram no PDF (nem PDF nem imagem): {ignorados.join(", ")}.</span>}
                                </p>
                            )}
                            {itens.length === 0 && (
                                <p className="py-6 text-center text-sm text-muted-foreground">Nenhum anexo (PDF ou imagem) para juntar.</p>
                            )}

                            {itens.map((item, indice) => (
                                <div key={item.id ?? indice} className="rounded-lg border border-border p-3 flex flex-col gap-3">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="min-w-0">
                                            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                {indice + 1}. {item.descricao}
                                            </span>
                                            <span className="block text-sm text-foreground truncate" title={item.nome}>
                                                <i className={`mr-1 ${item.tipo === "pdf" ? "fa fa-file-pdf text-red-600" : "fa fa-image text-primary"}`} />
                                                {item.nome}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-3">
                                            {item.incluir && (
                                                <div className="inline-flex items-center gap-1">
                                                    <button type="button" title="Girar 90° para a esquerda" aria-label="Girar para a esquerda"
                                                        disabled={item.girando || gerando} onClick={() => girar(indice, -90)}
                                                        className="w-8 h-8 rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-50">
                                                        <i className="fa fa-rotate-left" />
                                                    </button>
                                                    <button type="button" title="Girar 90° para a direita" aria-label="Girar para a direita"
                                                        disabled={item.girando || gerando} onClick={() => girar(indice, 90)}
                                                        className="w-8 h-8 rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-50">
                                                        <i className="fa fa-rotate-right" />
                                                    </button>
                                                    {item.girando && <i className="fa fa-circle-notch fa-spin text-primary ml-1" />}
                                                    {item.tipo === "pdf" && item.rotacao !== 0 && (
                                                        <span className="text-xs text-muted-foreground ml-1">Girado {item.rotacao}°</span>
                                                    )}
                                                </div>
                                            )}
                                            {item.tipo === "imagem" && !pontosSaoPadrao(item.pontos) && item.incluir && (
                                                <button type="button" className="text-xs text-primary hover:underline"
                                                    onClick={() => atualizarItem(indice, { pontos: PONTOS_PADRAO })}>
                                                    <i className="fa fa-undo mr-1" />Restaurar cantos
                                                </button>
                                            )}
                                            <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer select-none">
                                                <input type="checkbox" checked={item.incluir}
                                                    onChange={e => atualizarItem(indice, { incluir: e.target.checked })} />
                                                Incluir no PDF
                                            </label>
                                        </div>
                                    </div>
                                    {item.incluir && item.tipo === "imagem" && (
                                        <RecorteQuatroPontos
                                            blob={item.blob}
                                            pontos={item.pontos}
                                            onChange={pontos => atualizarItem(indice, { pontos })}
                                            desabilitado={gerando || item.girando}
                                        />
                                    )}
                                    {item.incluir && item.tipo === "pdf" && (
                                        <p className="text-xs text-muted-foreground">PDF — entra com todas as páginas (o giro vale para todas). Veja na visualização.</p>
                                    )}
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </Dialog>
    );
}
